<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      v-if="!inPopup"
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          <template v-if="isInquiriesView">{{ $t('ui.inquiries') }}</template>
          <template v-else-if="isCustomerOrdersView">{{ $t('ui.customerOrders') }}</template>
          <template v-else-if="isOrdersView || this.userIsWarehouseman">
            <template v-if="isIndentList">
              {{ $t('order.ordersIndent') }}
            </template>
            <template v-else>
              {{ $t('order.orders') }}
            </template>
          </template>
          <template v-else-if="isOffersView && isIndentList">{{ $t('order.indentOffers') }}</template>
          <template v-else-if="isOffersView && !isIndentList">{{ $t('order.offers') }}</template>
        </span>
        <h3 class="page-title">
          <template v-if="isCustomerOrdersView">
            {{ $t('ui.customerOrdersList') }}
          </template>
          <template v-if="$route.path === '/do-sprawdzenia' && this.userIsWarehouseman">
            {{ $t('order.toCheckInWarehouse') }}
          </template>
          <template v-if="$route.path === '/oczekujace-na-dostawe' && this.userIsWarehouseman">
            {{ $t('order.pendingProductsFromSupplier') }}
          </template>
          <template v-else>
            <template v-if="isInquiriesView">{{ $t('order.offerInquiriessList') }}</template>
            <template v-else-if="isOrdersView">
              <template v-if="isIndentList">
                {{ $t('order.ordersListIndent') }}
              </template>
              <template v-else>
                {{ $t('order.ordersList') }}
              </template>
            </template>
            <template v-else-if="isOffersView && isIndentList">{{ $t('order.offersListIndent') }}</template>
            <template v-else-if="isOffersView && !isIndentList">{{ $t('order.offersList') }}</template>
          </template>
        </h3>
      </d-col>

      <d-col
        v-if="!userIsWarehouseman && !userIsCustomer && (isOffersView || isOrdersView)"
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-checkbox
          v-if="userIsWorker"
          inline
          v-model="filters.show_all"
          toggle
          toggle-small>
          {{ $t('order.showAllOrders') }}
        </d-checkbox>

        <d-button
          v-if="isOffersView"
          theme="success"
          @click.prevent="$router.push(isIndentList ? '/oferty-indent/nowa' : '/oferty/nowa')">
          <i class="material-icons">add</i>
          {{ $t('order.addNewOffer') }}
        </d-button>

        <d-button
          v-if="isOrdersView"
          theme="success"
          outline
          class="ml-2"
          @click.prevent="$router.push(addNewUrl)">
          <i class="material-icons">add</i>
          <template v-if="isIndentList">
            {{ $t('order.addNewOrderIndent') }}
          </template>
          <template v-else>
            {{ $t('order.addNewOrder') }}
          </template>
        </d-button>
      </d-col>

      <d-col
        v-if="userIsCustomer && isInquiriesView"
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button theme="success" @click.prevent="$router.push('/zapytania/nowe')">
          <i class="material-icons">add</i>
          {{ $t('order.addNewInquiry') }}
        </d-button>
      </d-col>
    </d-row>

    <d-row
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="orders-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex orders-filter">
        <div class="d-flex mb-2">
          <d-checkbox
            v-if="userIsWorker && inPopup && popupShowAllUI"
            inline
            v-model="filters.show_all"
            toggle
            toggle-small
            class="is-moved-to-top">
            {{ $t('order.showAllOrders') }}
          </d-checkbox>

          <d-input
            v-model="filters.order_name"
            :placeholder="isInquiriesView ? $t('order.inquiryName') : $t('ui.orderName')"
            class="w-25" />

          <d-input
            v-if="!isInquiriesView && !userIsWarehouseman"
            v-model="filters.settlement_number"
            :placeholder="$t('order.settlementNumber')"
            class="ml-2 w-25" />

          <d-input
            v-if="!userIsWarehouseman && !isCustomerOrdersView"
            v-model="filters.tracking_number"
            :placeholder="$t('order.trackingNumber')"
            class="ml-2 w-25" />

          <date-range-picker
            ref="picker"
            :locale-data="{
              'firstDay': 1,
              'format': 'DD-MM-YYYY',
              'daysOfWeek': [$t('ui.daysOfWeek.sun'), $t('ui.daysOfWeek.mon'), $t('ui.daysOfWeek.tue'), $t('ui.daysOfWeek.wed'), $t('ui.daysOfWeek.thur'), $t('ui.daysOfWeek.fri'), $t('ui.daysOfWeek.sat')],
              'monthNames': [$t('ui.monthNames.jan'), $t('ui.monthNames.feb'), $t('ui.monthNames.mar'), $t('ui.monthNames.apr'), $t('ui.monthNames.may'), $t('ui.monthNames.jun'), $t('ui.monthNames.jul'), $t('ui.monthNames.aug'), $t('ui.monthNames.sep'), $t('ui.monthNames.oct'), $t('ui.monthNames.nov'), $t('ui.monthNames.dec')],
            }"
            class="ml-2 w-25"
            style="min-width: 200px;"
            :singleDatePicker="false"
            :timePicker="false"
            :showWeekNumbers="false"
            :showDropdowns="false"
            :autoApply="true"
            v-model="dateRange"
            :ranges="false"
            :linkedCalendars="true">
            <template v-slot:input="picker" style="min-width: 350px;">
              {{ formatDateStart(picker.startDate) }} - {{ formatDateEnd(picker.endDate) }}
            </template>
          </date-range-picker>

          <v-select
            v-if="userIsWarehouseman"
            class="ml-2 w-25"
            :options="orderNewOrderOptions"
            v-model="filters.warehouse_panel_new_orders"
            :placeholder="$t('order.newOrdersQuestionMark')"
            :searchable="false"
            label="text">
          </v-select>

          <v-select
            v-if="userIsWarehouseman"
            class="ml-2 w-25"
            :options="orderRepeatedOrderOptions"
            v-model="filters.warehouse_panel_is_repeated"
            :placeholder="$t('order.repeatedOrdersQuestionMark')"
            :searchable="false"
            label="text">
          </v-select>

          <v-select
            v-if="!userIsWarehouseman && popupStatus === '' && !isInquiriesView && !isCustomerOrdersView"
            class="ml-2 w-50"
            :options="orderStatusOptions"
            v-model="filters.status"
            :placeholder="$t('status.statusQuestionMark')"
            :searchable="false"
            label="text">
          </v-select>

          <v-select
            v-if="isCustomerOrdersView"
            class="ml-2 w-25"
            :options="orderPriorityOptions"
            v-model="filters.is_priority"
            :placeholder="$t('order.isPriority')"
            :searchable="false"
            label="text">
          </v-select>

          <v-select
            v-if="isCustomerOrdersView"
            class="ml-2 w-25"
            :options="orderWorkerOptions"
            v-model="filters.user_id"
            :placeholder="$t('order.isWorker')"
            :searchable="false"
            label="text">
          </v-select>
        </div>

        <div
          class="d-flex mb-2"
          v-if="!userIsWarehouseman && !userIsCustomer && !isCustomerOrdersView">
          <v-select
            class="w-25 mr-2"
            :options="orderRepeatedOptions"
            v-model="filters.is_repeated"
            :placeholder="$t('order.isRepeated')"
            :searchable="false"
            label="text">
          </v-select>

          <v-select
            class="w-50 mr-2"
            :options="orderTagsOptions"
            v-model="filters.orderTags"
            :placeholder="$t('order.orderTags')"
            :searchable="true"
            :multiple="true"
            label="text">
            <template slot="no-options">
              {{ $t('order.noTagsMatchingGivenPhrase') }}&hellip;
            </template>
          </v-select>

          <v-select
            v-if="userIsAdminOrWorker"
            class="w-25 mr-2"
            :options="orderIECommunicateOptions"
            v-model="filters.ie_communicate"
            :placeholder="$t('order.ieCommunicate')"
            :searchable="false"
            label="text">
          </v-select>

          <v-select
            v-if="userIsAdminOrWorker && !isCustomerOrdersView"
            class="w-25"
            :options="servicesReceivedOptions"
            v-model="filters.services_received"
            :placeholder="$t('order.servicesReceived') + '?'"
            :searchable="false"
            label="text">
          </v-select>
        </div>

        <div
          v-if="!userIsWarehouseman"
          class="d-flex mb-2">
          <select-customer
            v-if="!userIsWarehouseman && !userIsCustomer && !(inPopup && forcedCustomerID)"
            :customerID="filters.customer_id"
            class="w-50" />

          <v-select
            v-if="!userIsWarehouseman && !userIsCustomer && !(inPopup && forcedCustomerID)"
            class="ml-2 w-50"
            :options="countriesOptions"
            v-model="filters.country_id"
            :placeholder="$t('customer.customerCountryQuestionMark')"
            :searchable="false"
            label="text">
          </v-select>

          <v-select
            v-if="!userIsWarehouseman && !userIsCustomer && !(inPopup && forcedCustomerID)"
            class="ml-2 w-50"
            :options="customerTagsOptions"
            v-model="filters.customerTags"
            :placeholder="$t('order.customerTags')"
            :searchable="true"
            :multiple="true"
            label="text">
            <template slot="no-options">
              {{ $t('order.noTagsMatchingGivenPhrase') }}&hellip;
            </template>
          </v-select>

          <v-select
            v-if="!userIsWarehouseman && popupStatus === '' && !isInquiriesView && !isCustomerOrdersView"
            class="ml-2 w-50"
            :options="purchaseStatusOptions"
            v-model="filters.purchase_status"
            :placeholder="$t('status.purchaseStatusQuestionMark')"
            :searchable="false"
            label="text">
          </v-select>

          <v-select
            v-if="!userIsWarehouseman && !userIsCustomer && inPopup && !isInquiriesView && !isCustomerOrdersView"
            class="ml-2 w-50"
            :options="orderDelayedSampleOptions"
            v-model="filters.sampleRequirementOverdue"
            :placeholder="$t('order.isDelayedSample')"
            :searchable="false"
            label="text">
          </v-select>
        </div>

        <div
          v-if="!userIsWarehouseman && !isCustomerOrdersView"
          class="d-flex mb-2">
          <v-select
            v-if="!userIsWarehouseman && !userIsCustomer && popupStatus === '' && !isInquiriesView"
            class="w-25"
            :options="orderPriorityOptions"
            v-model="filters.is_priority"
            :placeholder="$t('order.isPriority')"
            :searchable="false"
            label="text">
          </v-select>

          <v-select
            v-if="!userIsWarehouseman && !userIsCustomer && popupStatus === '' && !isInquiriesView"
            class="ml-2 w-25"
            :options="orderExclamationOptions"
            v-model="filters.exclamation"
            :placeholder="$t('order.isDelayed')"
            :searchable="false"
            label="text">
          </v-select>

          <v-select
            v-if="!userIsWarehouseman && !userIsCustomer && popupStatus === '' && !isInquiriesView"
            class="ml-2 w-25"
            :options="orderBlockadeOptions"
            v-model="filters.blockade"
            :placeholder="$t('order.blockedQuestionMark')"
            :searchable="false"
            label="text">
          </v-select>

          <v-select
            v-if="!userIsWarehouseman && !userIsCustomer && popupStatus === '' && !isInquiriesView"
            class="ml-2 w-25"
            :options="orderSLBlockadeOptions"
            v-model="filters.softlab_blockade_status"
            :placeholder="$t('order.softlabBlockadeStatus') + '?'"
            :searchable="false"
            label="text">
          </v-select>

          <v-select
            v-if="!userIsWarehouseman && !userIsCustomer && popupStatus === '' && !isInquiriesView"
            class="ml-2 w-25"
            :options="orderWorkerOptions"
            v-model="filters.user_id"
            :placeholder="$t('order.isWorker')"
            :searchable="false"
            label="text">
          </v-select>
        </div>

        <div class="d-flex">
          <v-select
            v-if="userIsWarehouseman"
            class="w-25"
            :options="orderChangedOptions"
            v-model="filters.warehouse_panel_updated_orders"
            :placeholder="$t('order.orderChangedStatus') + '?'"
            :searchable="false"
            label="text">
          </v-select>

          <v-select
            v-if="userIsWarehouseman"
            class="ml-2 w-50"
            :options="warehousemans"
            v-model="filters.collected_by_user_id"
            :placeholder="$t('order.selectWarehouseman')"
            :searchable="false"
            label="name">
          </v-select>

          <v-select
            v-if="!userIsWarehouseman && !userIsCustomer && !isInquiriesView && !isCustomerOrdersView"
            class="w-25"
            :options="contractors"
            v-model="filters.contractor_id"
            :placeholder="$t('order.isContractor')"
            :reduce="contractor => contractor.id"
            :searchable="false"
            label="name">
          </v-select>

          <v-select
            v-if="!userIsWarehouseman && !isInquiriesView && !isCustomerOrdersView"
            class="ml-2 w-25"
            :options="paidStatusOptions"
            v-model="filters.settlement"
            :placeholder="$t('ui.isSettled')"
            :searchable="false"
            label="text">
          </v-select>

          <v-select
            v-if="!userIsWarehouseman && !isInquiriesView && !isCustomerOrdersView"
            class="ml-2 w-25"
            :options="erpStatusOptions"
            v-model="filters.is_erp_synchronised"
            :placeholder="$t('order.erp.isErpSynchronised')"
            :searchable="false"
            label="text">
          </v-select>

          <v-select
            v-if="!userIsWarehouseman && !isInquiriesView && !isCustomerOrdersView"
            class="ml-2 w-25"
            :options="orderSpecialRequirementOptions"
            v-model="filters.is_special_requirement"
            :placeholder="$t('order.hasSpecialRequirementMark')"
            :searchable="false"
            label="text">
          </v-select>

          <v-select
            v-if="!userIsWarehouseman && !userIsCustomer"
            class="ml-2 w-25"
            :options="invoiceRequestStatusOptions"
            v-model="filters.invoice_request"
            :placeholder="$t('order.invoiceRequestStatusQuestionMark')"
            :searchable="false"
            label="text">
          </v-select>
        </div>

        <div class="d-flex mt-2">
          <v-select
            v-if="!isInquiriesView"
            class="w-50"
            :options="productCodesOptions"
            v-model="filters.productCode"
            ref="product-codes-filter"
            :placeholder="$t('product.productsCodes')"
            :searchable="true"
            :multiple="true"
            label="text"
            :filter="filteredProductCodes">
            <template slot="no-options">
              <template v-if="$refs['product-codes-filter'] && $refs['product-codes-filter'].search.length">
                {{ $t('product.noProductsMatchingGivenPhrase') }}&hellip;
              </template>
              <template v-else>
                {{ $t('product.noProductsMatchingGivenPhrase') }}
              </template>
            </template>
          </v-select>

          <v-select
            v-if="!isInquiriesView"
            class="ml-2 mr-2 w-50"
            :options="productTagsOptions"
            v-model="filters.productTags"
            :placeholder="$t('order.productsTags')"
            :searchable="true"
            :multiple="true"
            label="text">
            <template slot="no-options">
              {{ $t('order.noTagsMatchingGivenPhrase') }}&hellip;
            </template>
          </v-select>

          <v-select
            v-if="!userIsWarehouseman && !isInquiriesView && !isCustomerOrdersView"
            class="mr-2 w-25"
            :options="hasSaleProductsOptions"
            v-model="filters.has_sale_products"
            :placeholder="$t('order.hasSaleProductsQuestion')"
            :searchable="false"
            label="text">
          </v-select>

          <d-button
            theme="info"
            outline
            class="text-nowrap ml-auto"
            @click.prevent="validateFiltersForm">{{ $t('ui.search') }}</d-button>

          <d-button
            theme="danger"
            outline
            class="text-nowrap ml-2"
            @click.prevent="clearFilters">{{ $t('ui.resetFilters') }}</d-button>

          <d-button
            v-if="!userIsWarehouseman && !userIsCustomer"
            theme="success"
            class="ml-2 text-nowrap"
            @click.prevent.stop="loadFilteredData(true)">
            <div
              v-if="downloadInProgress"
              class="spinner-border spinner-border-sm"
              role="status"></div>
            <template v-if="!downloadInProgress">
              {{ $t('order.downloadXls') }}
            </template>
          </d-button>
        </div>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">{{ $t('order.customerAndName') }}</th>
            <th v-if="popupFilter === 'WITHOUT_SETTLEMENT'" scope="col" class="border-0" style="width: 180px;">{{ $t('order.problemWithInvoice') }}</th>

            <template v-if="inPopup">
              <th scope="col" class="border-0" style="width: 240px;">
                <template v-if="isInquiriesView">{{ $t('order.inquirySubmissionDate') }}</template>
                <template v-else-if="isOrdersView">{{ $t('order.orderSubmissionDate') }}</template>
                <template v-else>{{ $t('order.offerSubmissionDate') }}</template>
              </th>
              <th scope="col" class="border-0" style="width:180px;">
                {{ $t('order.sampleRequirementDate') }}
              </th>
              <th scope="col" class="border-0" style="width: 120px;">
                {{ $t('order.sampleRequirementDays') }}
              </th>
              <th scope="col" class="border-0" style="width: 180px;">{{ $t('status.status') }}</th>
              <th scope="col" class="border-0" style="width: 160px;" v-if="!userIsWarehouseman && !isInquiriesView">{{ $t('ui.value') }}</th>
            </template>
            <template v-else>
              <th scope="col" class="border-0" style="width: 180px;">{{ $t('status.status') }}</th>
              <th scope="col" class="border-0" style="width: 160px;" v-if="!userIsWarehouseman && !isInquiriesView">{{ $t('ui.value') }}</th>
              <th scope="col" class="border-0" style="width: 240px;">
                <template v-if="isInquiriesView">{{ $t('order.inquirySubmissionDate') }}</template>
                <template v-else-if="isOrdersView">{{ $t('order.orderSubmissionDate') }}</template>
                <template v-else>{{ $t('order.offerSubmissionDate') }}</template>
              </th>
            </template>

            <th scope="col" class="border-0" :style="actionsColumnWidth">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="!loadError">
          <tr
            v-for="(order, index) of orders"
            :key="'order-' + index"
            :class="{
              'has-shipping-warning': hasShippingDateWarning(order),
              'has-blockade-warning': hasBlockadeDateWarning(order),
              'has-unpaid-advance': hasUnpaidAdvance(order),
              'has-been-last-visited': lastViewedItem.id === order.id,
              'has-deadline': !!order.deadline && userIsAdminOrWorker
            }">
            <td class="align-middle customer-info">
              <d-badge
                v-if="order.is_priority"
                theme="warning"
                class="mb-2 mr-2">
                {{ $t('order.priorityOrder') }}
              </d-badge>
              <d-badge
                v-if="order.collect_alert"
                theme="danger"
                class="mb-2 mr-2">
                {{ $t('order.ordersCollectedPartially') }}
              </d-badge>
              <d-badge
                v-if="order.products.filter(product => !!product.container_id).length"
                theme="danger"
                outline
                class="mb-2 mr-2">
                {{ $t('container.container') }}
              </d-badge>
              <d-badge
                v-if="order.products.filter(product => !!product.purchase_id).length"
                theme="warning"
                outline
                class="mb-2 mr-2">
                {{ $t('order.orderFromSupplier') }}
              </d-badge>
              <d-badge
                v-if="order.blockade_status === 'BLOCKADE'"
                theme="danger"
                class="mb-2 mr-2">
                {{ $t('order.blockade') }}
              </d-badge>
              <d-badge
                v-if="order.blockade_status === 'RETURN_TO_STOCK'"
                theme="danger"
                class="mb-2 mr-2">
                {{ $t('order.returnToStock') }}
              </d-badge>
              <d-badge
                v-if="userIsWarehouseman && order.collected_again"
                theme="danger"
                class="mb-2 mr-2">
                {{ $t('order.collectedAgain') }}
              </d-badge>
              <d-badge
                v-if="userIsAdminOrWorker && order.is_virtual"
                theme="warning"
                :outline="true"
                class="mb-2 mr-2">
                {{ $t('order.isVirtual') }}
              </d-badge>
              <d-badge
                v-if="userIsAdminOrWorker && order.is_advance"
                theme="danger"
                class="mb-2 mr-2">
                {{ $t('order.badPayerRequiresAdvance') }}
              </d-badge>
              <d-badge
                v-if="userIsAdminOrWorker && order.is_advance && !order.advance_paid"
                theme="danger"
                class="mb-2">
                {{ $t('order.advanceNotPaid') }}
              </d-badge>
              <d-badge
                v-if="userIsAdminOrWorker && order.services_received"
                theme="success"
                class="mb-2 mr-2">
                {{ $t('order.servicesReceived') }}
              </d-badge>
              <br class="br-to-hide">
              {{ order.customer_name }}<br v-if="order.order_name">
              <small v-if="order.order_name">
                {{ order.order_name }}
                <template v-if="order.order_name_notes">
                  {{ order.order_name_notes }}
                </template>
              </small><br>
              <small v-if="order.deadline">
                <span v-if="!hasDeadlineWarning(order)"><strong>{{ $t('ui.deadline') }}:</strong> {{ order.deadline }}</span>
                <span v-else class="text-danger"><strong>{{ $t('ui.deadline') }}:</strong> <i class="material-icons">warning</i> {{ order.deadline }}</span>
              </small>
              <br v-if="order.shipper_date">
              <small v-if="order.shipper_date">
                <span v-if="!hasShippingDateWarning(order)"><strong>{{ $t('order.shipment') }}:</strong> {{ order.shipper_date }}</span>
                <span v-else class="text-danger"><strong>{{ $t('order.shipment') }}:</strong> <i class="material-icons">warning</i> {{ order.shipper_date }}</span>
              </small>

              <br v-if="order.blockade_status && order.blocked_at">
              <small v-if="order.blockade_status && order.blocked_at">
                <span v-if="!hasBlockadeDateWarning(order)"><strong>{{ $t('order.blockade') }}:</strong> {{ order.blocked_at.split(' ')[0] }}</span>
                <span v-else class="text-danger"><strong>{{ $t('order.blockade') }}:</strong> <i class="material-icons">warning</i> {{ order.blocked_at.split(' ')[0] }}</span>
              </small>

              <small v-if="!userIsCustomer && !order.order_name && order.is_inquiry">
                <span class="text-danger">
                  <i class="material-icons">warning</i>
                  {{ $t('order.orderPlacedByClientMissingInfo') }}
                </span>
              </small>

              <br v-if="!userIsCustomer && order.is_inquiry">
              <span
                v-if="!userIsCustomer && order.is_inquiry"
                class="is-inquiry-info text-primary">
                <i class="material-icons">info</i> {{ $t('order.createdFromOfferInquiry') }}
              </span>

              <br v-if="userIsWarehouseman && order.description_for_warehouse">
              <small v-if="userIsWarehouseman && order.description_for_warehouse">
                <strong>{{ $t('order.commentsForWarehouseman') }}:</strong> {{ order.description_for_warehouse }}
              </small>

              <br v-if="showWorkers && order.user_name">
              <small v-if="showWorkers && order.user_name">
                {{ $t('users.worker') }}: <span class="text-primary">{{ order.user_name }}</span>
              </small>

              <div
                v-if="order.purchases && order.purchases.length && userIsAdminOrWorker"
                class="purchase-info">
                <div
                  v-for="(purchase, index) of order.purchases"
                  :key="'order-purchase-' + order.id + '-' + index"
                  class="purchase-info-content">
                  <span>{{ $t('order.purchaseInfoStatus') }} <strong>{{ getPurchaseStatus(purchase.status) }}</strong></span>
                  <span v-if="purchase.file">{{ $t('order.purchaseInfoInvoiceFile') }}</span>
                  <span v-if="purchase.invoice_number">{{ $t('order.purchaseInfoInvoiceAdded') }} ({{ purchase.invoice_number }})</span>
                </div>
              </div>

              <d-badge
                v-if="userIsAdminOrWorker && (order.invoice_request || order.invoice_request_by_user)"
                theme="warning"
                class="mt-2">
                {{ $t('order.invoiceRequestSentText') }}
              </d-badge>

              <br v-if="userIsAdminOrWorker && order.editing_by">
              <d-badge
                v-if="userIsAdminOrWorker && order.editing_by"
                theme="warning"
                class="mb-2 mr-2">
                <i class="material-icons">lock</i>
                {{ $t('order.editInProgressBy', { user: order.edit_by.name }) }}
              </d-badge>

              <br v-if="userIsWorker && order.user_id !== getUserID">
              <span
                v-if="userIsWorker && order.user_id !== getUserID"
                class="text-danger mt-2 d-block">
                {{ $t('order.orderAssignedTo') }}: <strong>{{ order.user_name }}</strong>
              </span>

              <orders-list-boxes-status
                v-if="userIsWarehouseman && order.boxes"
                :boxes="order.boxes"
                :boxesCount="order.boxes_count"
                :boxesCollected="order.boxes_collected"
                :boxesCollectedBy="order.boxes_collected_by"
                :boxesCollectedCount="order.boxes_collected_count"
                :boxesCollectedToday="order.boxes_collected_today"
                :boxesReturned="order.boxes_returned"
                :boxesReturnedBy="order.boxes_returned_by"
                :boxesReturnedCount="order.boxes_returned_count"
                :boxesReturnedToday="order.boxes_returned_today"
                :boxesVerificationStatus="order.boxes_verification_status"
                :boxesVerified="order.boxes_verified"
                :boxesVerifiedBy="order.boxes_verified_by"
                :boxesVerifiedCount="order.boxes_verified_count"
                :boxesVerifiedToday="order.boxes_verified_today"
                :differentInProducts="order.different_in_products" />
            </td>

            <td
              v-if="popupFilter === 'WITHOUT_SETTLEMENT'"
              class="align-middle">
              <d-badge
                v-if="order.lack_invoice_reason === 'INVOICE'"
                theme="danger"
                outline>
                {{ $t('order.noInvoice') }}
              </d-badge>
              <d-badge
                v-if="order.lack_invoice_reason === 'FILE'"
                theme="danger"
                outline>
                {{ $t('order.noFile') }}
              </d-badge>
            </td>

            <template v-if="inPopup">
              <td class="align-middle">
                <template v-if="isOrdersView">
                  {{ order.order_created_at }}<br>
                </template>
                <template v-else>
                  {{ order.created_at }}<br>
                </template>
                <small>({{ $t('order.lastChange') }} {{ order.updated_at }})</small>
              </td>

              <td class="align-middle text-center">
                {{ order.sample_requirement_date }}
              </td>

              <td class="align-middle text-center">
                {{ order.sample_requirement_days }}
              </td>

              <td class="align-middle">
                <d-badge
                  :class="userIsAdminOrWorker ? 'cursor-pointer' : null"
                  :theme="getStatusClass(order.status)"
                  :outline="getOutline(order.status, order.collect_user_id)"
                  @click.native.prevent.stop="userIsAdminOrWorker ? showOrderStatusPopup(order.order_name, order.id, order.status) : false">
                  {{ getStatusName(order.status, order.collect_user_id) }}
                </d-badge><br>

                <d-badge
                  v-if="!order.is_virtual && !order.exclude_from_erp_integration"
                  :theme="order.erp_optima_updated_at ? 'success' : 'danger'"
                  class="w-100 mt-1"
                  :outline="true">
                  <template v-if="order.erp_optima_updated_at">
                    {{ $t('order.erp.integratedWithErp') }}
                  </template>
                  <template v-else>
                    {{ $t('order.erp.notIntegratedWithErp') }}
                  </template>
                </d-badge><br>

                <template v-if="order.is_special_requirement">
                  <d-badge
                    :id="'special-requirement-' + order.id"
                    theme="warning"
                    :outline="true"
                    style="cursor: help;">
                    {{ $t('order.hasSpecialRequirement') }}
                  </d-badge>
                  <d-tooltip
                    :target="'special-requirement-' + order.id"
                    :container="'special-requirement-wrapper-' + order.id">
                    {{ order.special_requirement }}
                  </d-tooltip>
                  <br>
                </template>

                <small
                  v-if="order.collect_warning"
                  class="text-danger">
                  <i class="material-icons">report_problem</i> {{ $t('order.foundDifferencesInCollectedProductsQuantity') }}<br>
                </small>

                <template v-if="!isAllWarehouseOrdersView && userIsWarehouseman && order.status === 'TO_COLLECT' && order.collect_user_id === 0">
                  <d-button
                    theme="success"
                    :disabled="!!takeOrderInProgress"
                    class="ml-auto mt-2 mb-1"
                    outline
                    size="sm"
                    @click.prevent="takeOrder(order.id)">
                    <template v-if="!takeOrderInProgress || takeOrderInProgress !== order.id">
                      {{ $t('order.iAcceptOrder') }}
                    </template>
                    <template v-if="takeOrderInProgress === order.id">
                      <div class="spinner-border spinner-border-sm" role="status"></div>
                    </template>
                  </d-button>
                  <br>
                </template>

                <small v-if="order.products && !isInquiriesView">
                  {{ $t('ui.productsGenitive') }}: <strong>{{ order.products.length }}</strong>
                </small>
                <br>
                <small v-if="!hasServicesWarnings(order) && !isInquiriesView">
                  {{ $t('order.servicesGenitive') }}: <strong>{{ getServicesCount(order.products, order.services) }}</strong>
                </small>
                <small v-else-if="!isInquiriesView" class="text-danger">
                  {{ $t('order.servicesGenitive') }}: <i class="material-icons">warning</i>
                  <strong>{{ getServicesCount(order.products, order.services) }}</strong>
                </small>

                <template v-if="order.synchronised_at && order.softlab_blockade_status">
                  <br>
                  <small>
                    {{ $t('order.softlabBlockadeStatus') }}:
                    <span
                      v-if="order.softlab_blockade_status === 'FULL'"
                      class="text-success">
                      {{ $t('order.softlabBlockadeStatusFull') }}
                    </span>
                    <span
                      v-if="order.softlab_blockade_status === 'PARTIAL'"
                      class="text-warning">
                      {{ $t('order.softlabBlockadeStatusPartial') }}
                    </span>
                  </small>
                </template>
              </td>

              <td
                v-if="!userIsWarehouseman && !isInquiriesView"
                class="align-middle">
                {{ Number(order.price).toFixed(2) }} {{ getCurrency(order.currency_id) }}<br>
                <small>
                  {{ $t('order.invoicesIssued') }}: <strong>{{ order.settlements.length }}</strong>
                </small>
              </td>
            </template>

            <template v-else>
              <td
                :id="'special-requirement-wrapper-' + order.id"
                class="align-middle">
                <d-badge
                  v-if="lastViewedItem.id === order.id"
                  theme="warning"
                  class="w-100 text-center mb-1">
                  <template v-if="lastViewedItem.action === 'viewed'">
                    {{ $t('order.lastItemViewed') }}
                  </template>
                  <template v-else>
                    {{ $t('order.lastItemModified') }}
                  </template>
                </d-badge>

                <d-badge
                  :class="userIsAdminOrWorker ? 'cursor-pointer' : null"
                  :theme="getStatusClass(order.status)"
                  :outline="getOutline(order.status, order.collect_user_id)"
                  class="w-100"
                  @click.native.prevent.stop="userIsAdminOrWorker ? showOrderStatusPopup(order.order_name, order.id, order.status) : false">
                  {{ getStatusName(order.status, order.collect_user_id) }}
                </d-badge>
                <br>

                <d-badge
                  v-if="!order.is_virtual && !order.exclude_from_erp_integration"
                  :theme="order.erp_optima_updated_at ? 'success' : 'danger'"
                  class="w-100 mt-1"
                  :outline="true">
                  <template v-if="order.erp_optima_updated_at">
                    {{ $t('order.erp.integratedWithErp') }}
                  </template>
                  <template v-else>
                    {{ $t('order.erp.notIntegratedWithErp') }}
                  </template>
                </d-badge>

                <small
                  v-if="order.exclude_from_erp_integration"
                  class="text-danger d-block mt-2" style="font-size:70%;">
                  {{ $t('order.erp.isExcluded') }}
                </small>

                <br v-if="!order.is_virtual && !order.exclude_from_erp_integration">

                <small v-if="order.latest_erp_optima_query && !order.exclude_from_erp_integration">
                  {{ $t('order.erp.latestQuery') }}:
                  <span
                    :class="{
                      'text-success': order.latest_erp_optima_query.status === 'DONE',
                      'text-warning': order.latest_erp_optima_query.status === 'WAITING',
                      'text-danger': order.latest_erp_optima_query.status === 'ERROR',
                      'text-primary': order.latest_erp_optima_query.status === 'QUEUED'
                    }"
                    :title="order.latest_erp_optima_query.status === 'ERROR' ? order.latest_erp_optima_query.error_message : null">
                    {{ $t('order.erp.latestQueryStatus.' + order.latest_erp_optima_query.status) }}
                  </span>
                </small>

                <br v-if="order.latest_erp_optima_query">

                <template v-if="order.is_special_requirement">
                  <d-badge
                    :id="'special-requirement-' + order.id"
                    theme="warning"
                    :outline="true"
                    style="cursor: help;">
                    {{ $t('order.hasSpecialRequirement') }}
                  </d-badge>
                  <d-tooltip
                    :target="'special-requirement-' + order.id"
                    :container="'special-requirement-wrapper-' + order.id">
                    {{ order.special_requirement }}
                  </d-tooltip>
                  <br>
                </template>

                <small
                  v-if="order.collect_warning"
                  class="text-danger">
                  <i class="material-icons">report_problem</i> {{ $t('order.foundDifferencesInCollectedProductsQuantity') }}<br>
                </small>

                <template v-if="!isAllWarehouseOrdersView && userIsWarehouseman && order.status === 'TO_COLLECT' && order.collect_user_id === 0">
                  <d-button
                    theme="success"
                    :disabled="!!takeOrderInProgress"
                    class="ml-auto mt-2 mb-1"
                    outline
                    size="sm"
                    @click.prevent="takeOrder(order.id)">
                    <template v-if="!takeOrderInProgress || takeOrderInProgress !== order.id">
                      {{ $t('order.iAcceptOrder') }}
                    </template>
                    <template v-if="takeOrderInProgress === order.id">
                      <div class="spinner-border spinner-border-sm" role="status"></div>
                    </template>
                  </d-button>
                  <br>
                </template>

                <template v-if="complaintsStatus(order.complaints)">
                  <d-badge
                    v-if="complaintsStatus(order.complaints) === 'IN_REALIZATION'"
                    class="mt-2 mb-1"
                    theme="danger">
                    {{ $t('complaints.complaint') }}
                  </d-badge>
                  <d-badge
                    v-else
                    class="mt-2 mb-1"
                    theme="success">
                    {{ $t('complaints.complaint') }}
                  </d-badge>
                  <br>
                </template>

                <small v-if="order.products && !isInquiriesView">
                  {{ $t('ui.productsGenitive') }}: <strong>{{ order.products.length }}</strong>
                </small>
                <br>
                <small v-if="!hasServicesWarnings(order) && !isInquiriesView">
                  {{ $t('order.servicesGenitive') }}: <strong>{{ getServicesCount(order.products, order.services) }}</strong>
                </small>
                <small v-else-if="!isInquiriesView" class="text-danger">
                  {{ $t('order.servicesGenitive') }}: <i class="material-icons">warning</i> <strong>{{ getServicesCount(order.products, order.services) }}</strong>
                </small>

                <template v-if="order.synchronised_at && order.softlab_blockade_status">
                  <br>
                  <small>
                    {{ $t('order.softlabBlockadeStatus') }}:
                    <span
                      v-if="order.softlab_blockade_status === 'FULL'"
                      class="text-success">
                      {{ $t('order.softlabBlockadeStatusFull') }}
                    </span>
                    <span
                      v-if="order.softlab_blockade_status === 'PARTIAL'"
                      class="text-warning">
                      {{ $t('order.softlabBlockadeStatusPartial') }}
                    </span>
                  </small>
                </template>
              </td>
              <td
                v-if="!userIsWarehouseman && !isInquiriesView"
                class="align-middle">
                {{ Number(order.price).toFixed(2) }} {{ getCurrency(order.currency_id) }}<br>
                <small>
                  {{ $t('order.invoicesIssued') }}: <strong>{{ order.settlements.length }}</strong>
                </small>
              </td>
              <td class="align-middle">
                <template v-if="isOrdersView">
                  {{ order.order_created_at }}<br>
                </template>
                <template v-else>
                  {{ order.created_at }}<br>
                </template>
                <small>({{ $t('order.lastChange') }} {{ order.updated_at }})</small>
              </td>
            </template>

            <td class="align-middle">
              <d-button
                v-if="!userIsWarehouseman && !userIsCustomer"
                theme="primary"
                outline
                @click.native="openOrderSummaryPopup(order.id)"
                class="mr-2"
                :title="$t('order.orderPreview')">
                <i class="material-icons">search</i>
              </d-button>
              <d-button
                v-if="!(inPopup && forcedCustomerID)"
                theme="primary"
                @click.native="orderActionUrl(order.id, order.is_indent, false)"
                class="mr-2"
                :title="!userIsWarehouseman && !userIsCustomer ? $t('ui.edit') : $t('order.see')">
                <i class="material-icons" v-if="!userIsWarehouseman && !userIsCustomer">edit</i>
                <i class="material-icons" v-if="userIsWarehouseman || userIsCustomer">visibility</i>
              </d-button>
              <d-button
                v-if="!userIsWarehouseman && !userIsCustomer"
                theme="primary"
                @click.native="orderActionUrl(order.id, order.is_indent, true)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">open_in_new</i>
              </d-button>
              <d-button
                v-if="!userIsWarehouseman && !userIsCustomer && !isCustomerOrdersView"
                theme="secondary"
                @click.native="duplicateOrder(order.id)"
                :disabled="duplicateInProgress || removeInProgress"
                class="mr-2"
                :title="$t('ui.duplicate')">
                <i class="material-icons">file_copy</i>
              </d-button>
              <d-dropdown
                v-if="!userIsWarehouseman && !userIsCustomer && !isCustomerOrdersView"
                icon="cloud_download"
                theme="info"
                class="mr-2">
                <d-dropdown-item>
                  <a
                    :disabled="downloadingPdf !== false"
                    @click.prevent="downloadPdf(order.id, order.order_name)"
                    :style="'display: block;' + ((downloadingPdf !== false) ? 'pointer-events: none;' : '')">
                    <template v-if="downloadingPdf !== order.id">{{ $t('ui.pdf') }}</template>
                    <template v-else>
                      <div class="spinner-border spinner-border-sm" role="status"></div>
                    </template>
                  </a>
                </d-dropdown-item>
                <d-dropdown-item>
                  <a
                    :disabled="downloadingXls !== false"
                    @click.prevent="downloadXls(order.id, order.status)"
                    :style="'display: block;' + ((downloadingXls !== false) ? 'pointer-events: none;' : '')">
                    <template v-if="downloadingXls !== order.id">{{ $t('ui.xls') }}</template>
                    <template v-else>
                      <div class="spinner-border spinner-border-sm" role="status"></div>
                    </template>
                  </a>
                </d-dropdown-item>
                <d-dropdown-item>
                  <a
                    :disabled="downloadingSummary !== false"
                    @click.prevent="downloadSummary(order.id, order.order_name)"
                    :style="'display: block;' + ((downloadingSummary !== false) ? 'pointer-events: none;' : '')">
                    <template v-if="downloadingSummary !== order.id">{{ $t('order.summary') }}</template>
                    <template v-else>
                      <div class="spinner-border spinner-border-sm" role="status"></div>
                    </template>
                  </a>
                </d-dropdown-item>
                <d-dropdown-item v-if="order.settlements.length > 0">
                  <a
                    :disabled="downloadingInvoice !== false"
                    @click.prevent="downloadInvoice(order)"
                    :style="'display: block;' + ((downloadingInvoice !== false) ? 'pointer-events: none;' : '')">
                    <template v-if="downloadingInvoice !== order.id && order.settlements.length === 1">{{ $t('order.invoicePdf') }}</template>
                    <template v-else-if="downloadingInvoice !== order.id && order.settlements.length > 1">{{ $t('order.invoiceZip') }}</template>
                    <template v-else>
                      <div class="spinner-border spinner-border-sm" role="status"></div>
                    </template>
                  </a>
                </d-dropdown-item>
              </d-dropdown>
              <d-button
                v-if="userIsWarehouseman"
                @click="downloadMarkingDoc(order.id)"
                :disabled="!!downloadingMarkingInProgress"
                theme="primary"
                class="mr-2"
                :title="$t('order.downloadOrderMarkingDoc')">
                <template v-if="downloadingMarkingInProgress !== order.id">
                  <i class="material-icons">sim_card_download</i>
                </template>
                <template v-else>
                  <div class="spinner-border spinner-border-sm" role="status"></div>
                </template>
              </d-button>
              <d-button
                v-if="userIsWarehouseman"
                @click="downloadSummary(order.id, order.order_name)"
                :disabled="!!downloadingSummary"
                theme="info"
                :title="$t('order.downloadOrderSummary')">
                <template v-if="downloadingSummary !== order.id">
                  <i class="material-icons">cloud_download</i>
                </template>
                <template v-else>
                  <div class="spinner-border spinner-border-sm" role="status"></div>
                </template>
              </d-button>
              <d-button
                v-if="userIsWarehouseman"
                @click="downloadAttachment(order.id)"
                :disabled="!!downloadingAttachment || !order.attachment"
                theme="warning"
                class="ml-2"
                :title="$t('order.downloadOrderAttachment')">
                <template v-if="downloadingAttachment !== order.id">
                  <i class="material-icons">description</i>
                </template>
                <template v-else>
                  <div class="spinner-border spinner-border-sm" role="status"></div>
                </template>
              </d-button>
              <d-button
                v-if="!userIsWarehouseman && !userIsCustomer"
                theme="danger"
                :disabled="!order.can_delete || removeInProgress || duplicateInProgress"
                @click.native="removeOrder(order.id)"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>

              <br>

              <d-button
                v-if="popupFilter === 'OBTAINING' && order.can_send_waiting_sample"
                theme="secondary"
                :outline="true"
                class="w-100 mt-2 text-center"
                @click.native="sendReminder(order.id, 'WAITING_SAMPLE')"
                :disabled="!!reminderSendingInProgress">
                <template v-if="reminderSendingInProgress !== order.id">
                  <i class="material-icons">mail</i>
                  {{ $t('order.sendReminder') }}
                </template>
                <div
                  v-if="reminderSendingInProgress === order.id"
                  class="spinner-border spinner-border-sm"
                  role="status"></div>
              </d-button>

              <small v-if="popupFilter === 'OBTAINING' && order.waiting_sample_date">
                {{ $t('order.lastSendReminderDate') }}: {{ order.waiting_sample_date }}
              </small>

              <d-button
                v-if="popupFilter === 'ACCEPTING' && order.can_send_waiting_accept"
                theme="secondary"
                :outline="true"
                class="w-100 mt-2 text-center"
                @click.native="sendReminder(order.id, 'WAITING_ACCEPT')"
                :disabled="!!reminderSendingInProgress">
                <template v-if="reminderSendingInProgress !== order.id">
                  <i class="material-icons">mail</i>
                  {{ $t('order.sendReminder') }}
                </template>
                <div
                  v-if="reminderSendingInProgress === order.id"
                  class="spinner-border spinner-border-sm"
                  role="status"></div>
              </d-button>

              <small v-if="popupFilter === 'ACCEPTING' && order.waiting_accept_date">
                {{ $t('order.lastSendReminderDate') }}: {{ order.waiting_accept_date }}
              </small>

              <d-button
                v-if="popupFilter === 'ENDING_PRODUCTION' && order.can_send_waiting_production"
                theme="secondary"
                :outline="true"
                class="w-100 mt-2 text-center"
                @click.native="sendReminder(order.id, 'WAITING_PRODUCTION')"
                :disabled="!!reminderSendingInProgress">
                <template v-if="reminderSendingInProgress !== order.id">
                  <i class="material-icons">mail</i>
                  {{ $t('order.sendReminder') }}
                </template>
                <div
                  v-if="reminderSendingInProgress === order.id"
                  class="spinner-border spinner-border-sm"
                  role="status"></div>
              </d-button>

              <small v-if="popupFilter === 'ENDING_PRODUCTION' && order.waiting_production_date">
                {{ $t('order.lastSendReminderDate') }}: {{ order.waiting_production_date }}
              </small>

              <d-button
                v-if="popupFilter === 'DRAFT' && order.can_send_waiting_draft"
                theme="secondary"
                :outline="true"
                class="w-100 mt-2 text-center"
                @click.native="sendReminder(order.id, 'WAITING_DRAFT')"
                :disabled="!!reminderSendingInProgress">
                <template v-if="reminderSendingInProgress !== order.id">
                  <i class="material-icons">mail</i>
                  {{ $t('order.sendReminder') }}
                </template>
                <div
                  v-if="reminderSendingInProgress === order.id"
                  class="spinner-border spinner-border-sm"
                  role="status"></div>
              </d-button>

              <small v-if="popupFilter === 'DRAFT' && order.waiting_draft_date">
                {{ $t('order.lastSendReminderDate') }}: {{ order.waiting_draft_date }}
              </small>
            </td>
          </tr>
          <tr v-if="orders.length === 0">
            <td
              colspan="8"
              class="text-center">
              <template v-if="isInquiriesView">{{ $t('order.noOfferInquriesToDisplay') }}&hellip;</template>
              <template v-else>{{ $t('order.noOffersOrOrdersToDisplay') }}&hellip;</template>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />

    <d-modal
      v-if="displayChangeStatusPopup"
      @close="displayChangeStatusPopup = false">
      <d-modal-header>
        <d-modal-title>
          {{ $t('order.changeOrderStatus') }}: {{ statusChangeOrderName }}
        </d-modal-title>
      </d-modal-header>
      <d-modal-body>
        <d-form>
          <div class="form-group">
            <label for="name">
              {{ $t('order.selectNewStatus') }}:
            </label>

            <div :class="{ 'form-control': true, 'is-select': true }">
              <v-select
                :options="orderStatusOptions"
                v-model="statusChangeOrderStatus"
                :searchable="false"
                :clearable="false"
                :disabled="statusChangeInProgress"
                :reduce="status => status.value"
                label="text">
              </v-select>
            </div>

            <d-alert
              :show="statusChangeOrderStatus === 'TO_DELETE' || statusChangeOrderStatus === 'DELETED'"
              class="text-center mt-4 mb-0"
              theme="danger">
              {{ $t('order.deletedStatusCannotBeChanged') }}
            </d-alert>

            <d-alert
              :show="statusChangeOrderStatus === 'TO_DELETE' || statusChangeOrderStatus === 'DELETED'"
              class="text-center mt-4 mb-0"
              theme="danger">
              {{ $t('order.reservationsInContainersAndSuppliersCancelledAfterStatusChange') }}
            </d-alert>
          </div>
        </d-form>
      </d-modal-body>
      <d-modal-footer>
        <d-button
          class="ml-0"
          theme="danger"
          :disabled="statusChangeInProgress"
          @click.prevent="displayChangeStatusPopup = false">
          {{ $t('ui.cancel') }}
        </d-button>
        <d-button
          class="ml-auto"
          theme="success"
          :disabled="statusChangeInProgress"
          @click.prevent="saveNewOrderStatus()">
          <div
            v-if="statusChangeInProgress"
            class="spinner-border spinner-border-sm"
            role="status"></div>
            {{ $t('ui.save') }}
        </d-button>
      </d-modal-footer>
    </d-modal>

    <order-summary-popup
      v-if="showOrderSummaryPopup"
      :orderID="orderSummaryID" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import CommonGetters from './../utils/CommonGetters.js';
import CustomerPanel from './../mixins/CustomerPanel.vue';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import orderStatusClasses from './../data/order-status-classes.js';
import orderStatusPhase from './../data/order-status-phase.js';
import orderStatuses from './../data/order-statuses.js';
import Pagination from './../components/common/Pagination.vue';
import SelectCustomer from './../components/common/SelectCustomer.vue';
import OrderSummaryPopup from './../components/orders/OrderSummaryPopup.vue';
import OrdersListBoxesStatus from './../components/orders/OrdersListBoxesStatus.vue';
import { mapGetters } from 'vuex';
import Vue from 'vue';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';

export default {
  name: 'orders',
  mixins: [
    CustomerPanel,
    HasFilters,
    HasPagination
  ],
  props: {
    inPopup: {
      default: false
    },
    popupFilter: {
      default: ''
    },
    popupStatus: {
      default: ''
    },
    showWorkers: {
      default: false
    },
    dashboardParam: {
      default: false
    },
    popupShowAllUI: {
      default: false
    },
    forcedCustomerID: {
      default: false
    },
    customerPopupContent: {
      default: false
    }
  },
  components: {
    'date-range-picker': DateRangePicker,
    'order-summary-popup': OrderSummaryPopup,
    'orders-list-boxes-status': OrdersListBoxesStatus,
    'pagination': Pagination,
    'select-customer': SelectCustomer
  },
  watch: {
    'filters.show_all': {
      handler () {
        if (this.allDataLoaded) {
          this.debouncedLoadFilteredData();
        }
      },
      deep: true
    }
  },
  computed: {
    ...mapGetters([
      'getUserID'
    ]),
    addNewUrl () {
      if (this.isIndentList) {
        return '/zamowienia-indent/nowe/';
      }

      return '/zamowienia/nowe/';
    },
    allDataLoaded () {
      return this.loaded && this.loadedCurrencies && this.loadedWorkers && this.loadedContractors && this.loadedProducts && this.loadedCustomerTags && this.loadedOrderTags && this.loadedWarehousemans;
    },
    userIsAdmin () {
      return this.$store.getters.getUserType === 'admin';
    },
    userIsAdminOrWorker () {
      return this.$store.getters.getUserType === 'admin' || this.$store.getters.getUserType === 'worker';
    },
    userIsWorker () {
      return this.$store.getters.getUserType === 'worker';
    },
    userIsWarehouseman () {
      return this.$store.getters.getUserType === 'warehouse_manager';
    },
    isCustomerOrdersView () {
      return this.$route.path === '/zamowienia-partnerow';
    },
    isInquiriesView () {
      return this.$route.path === '/zapytania';
    },
    isOrdersView () {
      return this.$route.path === '/zamowienia' || this.$route.path === '/zamowienia-indent' || (this.inPopup && this.customerPopupContent === 'orders');
    },
    isAllWarehouseOrdersView () {
      return this.$route.path === '/zamowienia-wszystkie';
    },
    isOffersView () {
      return this.$route.path === '/oferty' || this.$route.path === '/oferty-indent' || (this.inPopup && this.customerPopupContent === 'offers');
    },
    isIndentList () {
      return this.$route.path.indexOf('/zamowienia-indent') > -1 || this.$route.path.indexOf('/oferty-indent') > -1;
    },
    isOrdersList () {
      return this.$route.path === '/zamowienia';
    },
    countriesOptions () {
      return this.countries.map(country => ({
        text: country.name,
        value: country.id
      }));
    },
    hasSaleProductsOptions () {
      return [
        {
          text: this.$t('order.hasSaleProducts'),
          value: 1
        },
        {
          text: this.$t('order.hasNoSaleProducts'),
          value: 0
        }
      ];
    },
    orderIECommunicateOptions () {
      return [
        {
          text: this.$t('order.hasIECommunicate'),
          value: 1
        },
        {
          text: this.$t('order.hasNotIECommunicate'),
          value: 0
        }
      ];
    },
    orderPriorityOptions () {
      return [
        {
          text: this.$t('order.priority'),
          value: 1
        },
        {
          text: this.$t('order.noPriority'),
          value: 0
        }
      ];
    },
    orderRepeatedOptions () {
      return [
        {
          text: this.$t('order.repeated'),
          value: 1
        },
        {
          text: this.$t('order.notRepeated'),
          value: 0
        }
      ];
    },
    orderChangedOptions () {
      return [
        {
          text: this.$t('order.changed'),
          value: 1
        },
        {
          text: this.$t('order.notChanged'),
          value: 0
        }
      ];
    },
    orderExclamationOptions () {
      return [
        {
          text: this.$t('order.delayed'),
          value: 1
        }
      ];
    },
    orderBlockadeOptions () {
      return [
        {
          text: this.$t('order.blocked'),
          value: 'BLOCKADE'
        },
        {
          text: this.$t('order.returnToStock'),
          value: 'RETURN_TO_STOCK'
        }
      ];
    },
    orderSLBlockadeOptions () {
      return [
        {
          text: this.$t('order.softlabBlockadeStatus') + ': ' + this.$t('order.softlabBlockadeStatusFull'),
          value: 'FULL'
        },
        {
          text: this.$t('order.softlabBlockadeStatus') + ': ' + this.$t('order.softlabBlockadeStatusPartial'),
          value: 'PARTIAL'
        }
      ];
    },
    orderNewOrderOptions () {
      return [
        {
          text: this.$t('order.warehouseNewOrders'),
          value: 1
        },
        {
          text: this.$t('order.warehouseNotNewOrders'),
          value: 0
        }
      ];
    },
    orderSpecialRequirementOptions () {
      return [
        {
          text: this.$t('order.hasSpecialRequirement'),
          value: 1
        },
        {
          text: this.$t('order.hasNoSpecialRequirement'),
          value: 0
        }
      ];
    },
    orderRepeatedOrderOptions () {
      return [
        {
          text: this.$t('order.warehouseRepeatedOrders'),
          value: 1
        },
        {
          text: this.$t('order.warehouseNotRepeatedOrders'),
          value: 0
        }
      ];
    },
    orderDelayedSampleOptions () {
      return [
        {
          text: this.$t('order.delayedSamples'),
          value: 1
        },
        {
          text: this.$t('order.notDelayedSamples'),
          value: 0
        }
      ];
    },
    invoiceRequestStatusOptions () {
      return [
        {
          text: this.$t('order.optionInvoiceRequestSent'),
          value: 1
        },
        {
          text: this.$t('order.optionInvoiceRequestNotSent'),
          value: 0
        }
      ];
    },
    orderStatusOptions () {
      if (this.isOffersView) {
        return [
          {
            text: this.$t('order.newOffer'),
            value: 'NEW'
          },
          {
            text: this.$t('order.sentToCustomer'),
            value: 'SENT_TO_CUSTOMER'
          },
          {
            text: this.$t('order.offerClosed'),
            value: 'OFFER_CLOSED'
          }
        ]
      }

      return [
        {
          text: this.$t('order.toCollect'),
          value: 'TO_COLLECT'
        },
        {
          text: this.$t('order.waitingPurchaseProducts'),
          value: 'WAITING_PURCHASE_PRODUCTS'
        },
        {
          text: this.$t('ui.order'),
          value: 'ORDER'
        },
        {
          text: this.$t('order.inProduction'),
          value: 'IN_PRODUCTION'
        },
        {
          text: this.$t('order.toCheckInWarehouse'),
          value: 'TO_CHECK'
        },
        {
          text: this.$t('order.readyForShipping'),
          value: 'PREPARATION_FOR_SHIPPING'
        },
        {
          text: this.$t('order.waitingForShipping'),
          value: 'WAITING_FOR_SHIPPING'
        },
        {
          text: this.$t('order.sent'),
          value: 'SENT'
        },
        {
          text: this.$t('order.pendingPayment'),
          value: 'PENDING_PAYMENT'
        },
        {
          text: this.$t('order.ended'),
          value: 'ENDED'
        },
        {
          text: this.$t('order.toDelete'),
          value: 'TO_DELETE'
        },
        {
          text: this.$t('order.deleted'),
          value: 'DELETED'
        }
      ];
    },
    servicesReceivedOptions () {
      return [
        {
          text: this.$t('order.servicesReceived'),
          value: 1
        },
        {
          text: this.$t('order.servicesNotReceived'),
          value: 0
        }
      ];
    },
    customerTagsOptions () {
      return this.customerTags.map(tag => ({
        text: tag.name,
        value: tag.id
      }));
    },
    orderTagsOptions () {
      return this.orderTags.map(tag => ({
        text: tag.name,
        value: tag.id
      }));
    },
    productTagsOptions () {
      let options = [];

      for (let i = 0; i < this.productTags.length; i++) {
        for (let j = 0; j < this.productTags[i].tags.length; j++) {
          let tagName = this.productTags[i].tags[j].name;
          let tagID = this.productTags[i].tags[j].id;
          let tagCategoryID = this.productTags[i].tags[j].category_id;
          let tagCategoryName = this.productTags[i].name;

          let option = {
            text: tagCategoryName + ': ' + tagName,
            value: tagCategoryID + ';' + tagID
          };

          options.push(option);
        }
      }

      return options;
    },
    preloadedProductCodesOptions () {
      let options = [];

      for (let i = 0; i < this.products.length; i++) {
        let option = {
          text: (this.products[i].parent_id ? '🟩 ' : '⬜️ ') + this.products[i].code + ' - ' + this.products[i].name,
          value: '[#' + this.products[i].id + '#]' + this.products[i].code,
          valueLowerCase: ('[#' + this.products[i].id + '#]' + this.products[i].code).toLowerCase()
        };

        options.push(option);
      }

      return options;
    },
    productCodesOptions () {
      if (this.$refs['product-codes-filter'] && this.$refs['product-codes-filter'].search.length < 1) {
        return [];
      }

      return this.preloadedProductCodesOptions;
    },
    orderWorkerOptions () {
      return this.workers.map(worker => ({
        text: worker.name,
        value: worker.id
      }));
    },
    paidStatusOptions () {
      return [
        {
          text: this.$t('ui.settled'),
          value: 'PAID'
        },
        {
          text: this.$t('ui.notSettled'),
          value: 'UNPAID'
        }
      ];
    },
    erpStatusOptions () {
      return [
        {
          text: this.$t('order.erp.integratedWithErp'),
          value: 1
        },
        {
          text: this.$t('order.erp.notIntegratedWithErp'),
          value: 0
        },
        {
          text: this.$t('order.erp.isExcluded'),
          value: -1
        }
      ];
    },
    actionsColumnWidth () {
      if (this.userIsWarehouseman) {
        return 'width: 260px;';
      }

      if (this.userIsCustomer) {
        return 'width: 80px;';
      }

      if (this.isCustomerOrdersView) {
        return 'width: 250px;';
      }

      return 'width: 360px;';
    },
    subviewName () {
      if (this.isInquiriesView) {
        return 'inquiries';
      }

      if (this.isOffersView) {
        return 'offers';
      }

      return false;
    },
    purchaseStatusOptions () {
      return [
        {
          text: this.$t('purchase.newOrder'),
          value: 'NEW'
        },
        {
          text: this.$t('order.waitingPurchaseProducts'),
          value: 'WAITING_PRODUCTS'
        },
        {
          text: this.$t('order.pendingPayment'),
          value: 'WAITING_PAYMENT'
        },
        {
          text: this.$t('purchase.closed'),
          value: 'CLOSED'
        }
      ];
    }
  },
  data() {
    return {
      currencies: [],
      orders: [],
      workers: [],
      countries: [],
      contractors: [],
      productTags: [],
      customerTags: [],
      orderTags: [],
      products: [],
      warehousemans: [],
      loaded: false,
      loadedCountries: false,
      loadedCurrencies: false,
      loadedWorkers: false,
      loadedContractors: false,
      loadedWarehousemans: false,
      loadedProductTags: false,
      loadedProducts: false,
      loadedCustomerTags: false,
      loadedOrderTags: false,
      loadError: false,
      downloadingPdf: false,
      downloadingXls: false,
      downloadingSummary: false,
      downloadingAttachment: false,
      downloadingInvoice: false,
      removeInProgress: false,
      duplicateInProgress: false,
      takeOrderInProgress: false,
      skipLSPagination: false,
      confirmPurchaseProductsInProgress: false,
      dateRange: {
        startDate: null,
        endDate: null
      },
      filters: {
        order_name: '',
        tracking_number: '',
        settlement_number: '',
        customer_id: null,
        contractor_id: null,
        status: null,
        blockade: null,
        softlab_blockade_status: null,
        settlement: null,
        user_id: null,
        country_id: null,
        ie_communicate: null,
        is_priority: null,
        is_repeated: null,
        is_erp_synchronised: null,
        is_special_requirement: null,
        has_sale_products: null,
        exclamation: null,
        productTags: null,
        productCode: null,
        customerTags: null,
        orderTags: null,
        services_received: null,
        sampleRequirementOverdue: null,
        purchase_status: null,
        invoice_request: null,
        show_all: false,
        warehouse_panel_new_orders: null,
        warehouse_panel_is_repeated: null,
        collected_by_user_id: null,
        warehouse_panel_updated_orders: null
      },
      displayChangeStatusPopup: false,
      statusChangeOrderName: '',
      statusChangeOrderID: null,
      statusChangeOrderStatus: null,
      statusChangeInProgress: false,
      downloadInProgress: false,
      downloadingMarkingInProgress: false,
      reminderSendingInProgress: false,
      // order summary popup
      showOrderSummaryPopup: false,
      orderSummaryID: 0,
      // helpers
      lastViewedItem: {
        action: '',
        id: null
      }
    };
  },
  created () {
    if (this.inPopup) {
      this.skipLSPagination = true;
    }
  },
  mounted () {
    if (this.userIsWarehouseman) {
      this.retrieveLastViewedData();
    }

    if (!this.$root.orderUnblockInProgress) {
      this.loadFilteredData();
    }

    this.loadAdditionalData();
    this.$bus.$on('ui-select-customer-updated', this.setCustomerID);
    this.$bus.$on('order-summary-popup-open', this.openOrderSummaryPopup);
    this.$bus.$on('order-summary-popup-close', this.closeOrderSummaryPopup);
    this.$bus.$on('order-unblock-in-progress', this.loadFilteredDataOnUnblock);
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData (downloadAsFile = false) {
      let whereData = {};
      let allItemsParam = '';

      if (this.isIndentList) {
        whereData.is_indent = 1;
      }

      if (this.isOrdersList) {
        whereData.is_indent = 0;
      }

      if (this.filters.order_name && this.filters.order_name !== '') {
        whereData.order_name = this.filters.order_name;
      }

      if (this.filters.tracking_number && this.filters.tracking_number !== '') {
        whereData.tracking_number = this.filters.tracking_number;
      }

      if (this.filters.settlement_number && this.filters.settlement_number !== '') {
        whereData.settlement_number = this.filters.settlement_number;
      }

      if (this.filters.customer_id && this.filters.customer_id > 0) {
        whereData.customer_id = this.filters.customer_id;
      }

      if (this.inPopup && this.forcedCustomerID) {
        whereData.customer_id = this.forcedCustomerID;
      }

      if (this.filters.contractor_id && this.filters.contractor_id > 0) {
        whereData.contractor_id = this.filters.contractor_id;
      }

      if (this.filters.is_priority) {
        whereData.is_priority = +this.filters.is_priority.value;
      }

      if (this.filters.is_repeated) {
        whereData.is_repeated = +this.filters.is_repeated.value;
      }

      if (this.filters.ie_communicate) {
        whereData.ie_communicate = +this.filters.ie_communicate.value;
      }

      if (this.filters.country_id) {
        whereData.country_id = this.filters.country_id.value;
      }

      if (this.userIsWarehouseman && this.filters.collected_by_user_id) {
        whereData.collected_by_user_id = this.filters.collected_by_user_id.id;
      }

      if (this.userIsWarehouseman && this.filters.warehouse_panel_updated_orders) {
        whereData.warehouse_panel_updated_orders = this.filters.warehouse_panel_updated_orders.value;
      }

      if (this.dashboardParam) {
        whereData.dashboard = 1;
      }

      if (this.filters.productTags) {
        if (!whereData['products']) {
          whereData.products = {
            tags: []
          };
        }

        whereData['products']['tags'] = this.prepareProductTagsFilter(this.filters.productTags);
      }

      if (this.filters.has_sale_products && this.filters.has_sale_products.value !== null) {
        whereData.has_sale_products = +this.filters.has_sale_products.value;
      }

      if (this.filters.customerTags) {
        whereData['customer_tags'] = this.prepareCustomerTagsFilter(this.filters.customerTags);
      }

      if (this.filters.orderTags) {
        whereData['tags'] = this.prepareOrderTagsFilter(this.filters.orderTags);
      }

      if (this.filters.productCode) {
        if (!whereData['products']) {
          whereData.products = {
            code: []
          };
        }

        whereData['products']['code'] = this.prepareProductCodesFilter(this.filters.productCode);
      }

      if (this.inPopup) {
        whereData.order_by_is_priority = 1;
        whereData.exclamation = '';
      }

      if (this.filters.exclamation !== null) {
        whereData.exclamation = +this.filters.exclamation.value;
      }

      if (this.userIsWarehouseman && this.filters.warehouse_panel_new_orders) {
        whereData.warehouse_panel_new_orders = +this.filters.warehouse_panel_new_orders.value;
      }

      if (this.filters.is_erp_synchronised) {
        whereData.is_erp_synchronised = +this.filters.is_erp_synchronised.value;
      }

      if (this.filters.is_special_requirement) {
        whereData.is_special_requirement = +this.filters.is_special_requirement.value;
      }

      if (this.filters.status !== null) {
        whereData.status = this.filters.status.value;
      } else {
        if (this.$route.path === '/zamowienia' || this.$route.path === '/zamowienia/' || this.$route.path === '/zamowienia-indent' || this.$route.path === '/zamowienia-indent/' || (this.inPopup && this.customerPopupContent === 'orders')) {
          whereData.excluded_status = ['INQUIRY', 'NEW', 'OFFER_CLOSED', 'SENT_TO_CUSTOMER', 'DRAFT', 'CUSTOMER_ORDER'];
        }

        if (this.$route.path === '/zamowienia-partnerow') {
          whereData['status'] = ['CUSTOMER_ORDER'];
        }

        if (this.$route.path === '/zamowienia-partnerow') {
          whereData['status'] = ['CUSTOMER_ORDER'];
        }

        if (this.$route.path === '/oferty' || this.$route.path === '/oferty/' || this.$route.path === '/oferty-indent' || this.$route.path === '/oferty-indent/' || (this.inPopup && this.customerPopupContent === 'offers')) {
          whereData['status'] = ['NEW', 'SENT_TO_CUSTOMER', 'OFFER_CLOSED'];
        }

        if (this.$route.path === '/do-sprawdzenia' || this.$route.path === '/do-sprawdzenia/') {
          whereData['status'] = 'TO_CHECK';
        } else if (this.$route.path === '/zapytania' || this.$route.path === '/zapytania/') {
          whereData['status'] = 'INQUIRY';

          if (this.userIsCustomer) {
            whereData['status'] = ['INQUIRY', 'SENT_TO_CUSTOMER'];
          }
        } else if ((this.$route.path === '/zamowienia' || this.$route.path === '/zamowienia/' || this.$route.path === '/zamowienia-indent' || this.$route.path === '/zamowienia-indent/') && this.userIsWarehouseman) {
          whereData['status'] = ['TO_COLLECT', 'TO_DELETE', 'DELETED'];
        } else if ((this.$route.path === '/oczekujace-na-dostawe' || this.$route.path === '/oczekujace-na-dostawe/') && this.userIsWarehouseman) {
          whereData['status'] = 'WAITING_PURCHASE_PRODUCTS';
        }
      }

      if (this.isAllWarehouseOrdersView) {
        whereData['status'] = [];
        whereData['excluded_status'] = ['DRAFT'];
        allItemsParam = '?all_items=1';
      }

      if (this.filters.purchase_status !== null && typeof this.filters.purchase_status !== 'undefined') {
        whereData.purchase_status = this.filters.purchase_status.value;
      }

      if (this.filters.invoice_request !== null && typeof this.filters.invoice_request !== 'undefined') {
        whereData.invoice_request = this.filters.invoice_request.value;
      }

      if (this.filters.settlement !== null) {
        whereData['settlement-status'] = this.filters.settlement.value;
      }

      if (this.popupFilter !== '') {
        if (this.popupFilter === 'WITHOUT_SETTLEMENT') {
          whereData['without_settlement'] = 1;
        } else if (this.popupFilter === 'COLLECTED_BY_WAREHOUSEMAN') {
          whereData['collected_by_warehouseman'] = 1;
        } else if (this.popupFilter === 'OFFERS_TO_CHECK') {
          whereData['last_contact'] = 7;
        } else {
          whereData['warning'] = this.popupFilter;
        }
      }

      if (this.popupStatus !== '' && this.popupStatus !== 'BLOCKADE') {
        if (this.popupStatus === 'OFFERS_TO_CHECK') {
          whereData['status'] = ['NEW', 'SENT_TO_CUSTOMER', 'OFFER_CLOSED'];
        } else {
          whereData['status'] = this.popupStatus;
        }
      } else if (this.popupStatus !== '' && this.popupStatus === 'BLOCKADE') {
        whereData['blockade_status'] = 'BLOCKADE';
      }

      if (this.filters.blockade !== null) {
        whereData['blockade_status'] = this.filters.blockade.value;
      }

      if (this.filters.softlab_blockade_status) {
        whereData['softlab_blockade_status'] = this.filters.softlab_blockade_status.value;
      }

      if (this.filters.services_received) {
        whereData['services_received'] = this.filters.services_received.value;
      }

      if (this.popupFilter === 'OBTAINING') {
        whereData['blockade_status'] = '';
      }

      if (this.dateRange.startDate) {
        whereData['created_at_from'] = this.prepareDateString(this.dateRange.startDate);
      }

      if (this.dateRange.endDate) {
        whereData['created_at_to'] = this.prepareDateString(this.dateRange.endDate);
      }

      if (this.filters.user_id) {
        whereData.user_id = this.filters.user_id.value;
      }

      if (this.inPopup && this.filters.sampleRequirementOverdue !== null) {
        whereData['sample_requirement_overdue'] = +this.filters.sampleRequirementOverdue.value;
      }


      if (this.userIsWarehouseman && this.filters.warehouse_panel_is_repeated) {
        whereData['warehouse_panel_is_repeated'] = +this.filters.warehouse_panel_is_repeated.value;
      }

      if (downloadAsFile) {
        if (this.downloadInProgress) {
          return;
        }

        whereData['download_excel'] = 1;
        this.downloadInProgress = true;

        let requestConfig = {
          with: ['settlements', 'orderProducts', 'purchase', 'emails', 'complaints'],
          where: whereData
        };

        if (this.userIsWorker && this.filters.show_all) {
          requestConfig.show_all = 1;
        }

        this.$http.post(this.getApiEndpoint('/orders/items' + allItemsParam), requestConfig, {
          responseType: 'blob'
        }).then((response) => {
          let link = document.createElement('a');
          link.href = URL.createObjectURL(new Blob([response.data]));
          link.setAttribute('download', this.$t('order.ordersXlsFilename') + '.xlsx');

          if (this.inPopup) {
            document.querySelector('.big-popup .modal-body').appendChild(link);
            link.click();
            document.querySelector('.big-popup .modal-body').removeChild(link);
          } else {
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }

          this.downloadInProgress = false;
        }).catch(() => {
          this.downloadInProgress = false;
        });
      } else {
        let requestConfig = {
          with: ['settlements', 'orderProducts', 'purchase', 'emails', 'complaints'],
          where: whereData
        };

        if (this.userIsWorker && this.filters.show_all) {
          requestConfig.show_all = 1;
        }

        ListUtils.loadPaginatedItemsData(this, {
          endpoint: this.getApiEndpoint('/orders/items?page=' + this.currentPage + allItemsParam.replace('?', '&')),
          listField: 'orders',
          loadError: 'loadError',
          method: 'get',
          params: requestConfig,
          successAction () {
            Vue.nextTick(() => {
              if (document.querySelector('tr.has-been-last-visited')) {
                document.querySelector('tr.has-been-last-visited').scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                  inline: 'center'
                });
              }
            });
          }
        });
      }
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        order_name: '',
        tracking_number: '',
        settlement_number: '',
        customer_id: null,
        contractor_id: null,
        status: null,
        blockade: null,
        softlab_blockade_status: null,
        services_received: null,
        settlement: null,
        user_id: null,
        country_id: null,
        is_priority: null,
        is_repeated: null,
        is_erp_synchronised: null,
        is_special_requirement: null,
        has_sale_products: null,
        exclamation: null,
        productTags: null,
        customerTags: null,
        orderTags: null,
        productCode: null,
        sampleRequirementOverdue: null,
        purchase_status: null,
        invoice_request: null,
        show_all: false,
        collected_by_user_id: null,
        warehouse_panel_updated_orders: null
      };
      this.dateRange = {
        startDate: null,
        endDate: null
      };

      this.$bus.$emit('ui-select-customer-reset');
      this.$bus.$emit('view-filters-reset');
      this.$bus.$emit('pagination-reset', false);
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: this.getApiEndpoint('/currencies/items'),
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError'
      });

      if (!this.isInquiriesView) {
        FormUtils.loadAdditionalData(this, {
          method: 'get',
          endpoint: this.getApiEndpoint('/products/all-items-id-code'),
          loadedKey: 'loadedProducts',
          outputKey: 'products',
          errorKey: 'loadError',
          params: {
            where: {
              is_indent: +this.isIndentList
            }
          }
        });
      } else {
        this.loadedProducts = true;
      }

      if (!this.userIsCustomer && !this.userIsWarehouseman) {
        FormUtils.loadAdditionalData(this, {
          method: 'get',
          endpoint: '/customers/tags/items',
          loadedKey: 'loadedCustomerTags',
          outputKey: 'customerTags',
          errorKey: 'loadError',
          params: {
            return: 'get'
          }
        });
      } else {
        this.loadedCustomerTags = true;
      }

      if (!this.userIsCustomer && !this.userIsWarehouseman) {
        FormUtils.loadAdditionalData(this, {
          endpoint: '/orders/tags/items',
          loadedKey: 'loadedOrderTags',
          outputKey: 'orderTags',
          errorKey: 'loadError',
          params: {
            return: 'get'
          }
        });
      } else {
        this.loadedOrderTags = true;
      }

      if (this.userIsWarehouseman) {
        FormUtils.loadAdditionalData(this, {
          endpoint: this.getApiEndpoint('/users/items'),
          loadedKey: 'loadedWarehousemans',
          outputKey: 'warehousemans',
          errorKey: 'loadError',
          params: {
            where: {
              type: 'warehouseman'
            },
            return: 'get'
          }
        });
      } else {
        this.loadedWarehousemans = true;
      }

      if (!this.userIsCustomer) {
        FormUtils.loadAdditionalData(this, {
          endpoint: this.getApiEndpoint('/contractors/items'),
          loadedKey: 'loadedContractors',
          outputKey: 'contractors',
          errorKey: 'loadError',
          params: {
            return: 'get'
          }
        });

        FormUtils.loadAdditionalData(this, {
          endpoint: this.getApiEndpoint('/users/items'),
          loadedKey: 'loadedWorkers',
          params: {
            return: 'get',
            where: {
              type: ['worker', 'admin']
            }
          },
          outputKey: 'workers',
          errorKey: 'loadError'
        });

        FormUtils.loadAdditionalData(this, {
          endpoint: this.getApiEndpoint('/tags/categories/items'),
          loadedKey: 'loadedProductTags',
          params: {
            with: ['tags']
          },
          outputKey: 'productTags',
          errorKey: 'loadError'
        });

        FormUtils.loadAdditionalData(this, {
          method: 'get',
          endpoint: this.getApiEndpoint('/countries/items'),
          loadedKey: 'loadedCountries',
          outputKey: 'countries',
          errorKey: 'loadError'
        });
      } else {
        this.loadedCountries = true;
        this.loadedProductTags = true;
        this.loadedContractors = true;
        this.loadedWorkers = true;
      }
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['orders-filters-form'], this.debouncedLoadFilteredData);
    },
    removeOrder (orderID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: this.getApiEndpoint('/orders/delete/'),
        id: orderID,
        successTitle: this.$t('order.orderDeleteSuccessTitle'),
        successText: this.$t('order.orderDeleteSuccessText'),
        confirmTitle: this.$t('order.orderDeleteConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.orderDeleteErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.$bus.$emit('sidebar-counters-refresh');
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    duplicateOrder (orderID) {
      this.duplicateInProgress = true;

      Vue.swal({
        title: this.$t('order.duplicatePopup.title'),
        html: this.$t('order.duplicatePopup.text'),
        type: 'question',
        input: 'checkbox',
        inputPlaceholder: this.$t('order.duplicatePopup.copyFiles'),
        inputValue: false,
        showCancelButton: true,
        confirmButtonText: this.$t('order.duplicatePopup.duplicateOrder'),
        cancelButtonText: this.$t('ui.cancel')
      }).then((result) => {
        if (result.value === 1 || result.value === 0) {
          let duplicateData = {};

          if (result.value === 1) {
            duplicateData = {
              duplicate_fields: ['files']
            };
          }

          this.$http.post(this.getApiEndpoint('/orders/duplicate/' + orderID), duplicateData).then(response => {
            if (response.data.success) {
              Vue.swal({
                title: this.$t('order.itemDuplicateSuccess'),
                html: '',
                type: 'success',
                timer: 2000,
                confirmButtonText: this.$t('ui.ok'),
                buttonsStyling: true
              });

              this.loadFilteredData();
            } else {
              Vue.swal({
                title: this.$t('order.itemDuplicateError'),
                html: this.$t('ui.tryAgain'),
                type: 'warning',
                confirmButtonText: this.$t('ui.ok'),
                buttonsStyling: true
              });
            }

            this.duplicateInProgress = false;
          }).catch(error => {
            console.log(error);
            Vue.swal({
              title: this.$t('order.itemDuplicateError'),
              html: this.$t('ui.tryAgain'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });

            this.duplicateInProgress = false;
          });
        } else {
          this.duplicateInProgress = false;
        }
      });
    },
    // eslint-disable-next-line
    downloadPdf (orderID, orderName) {
      this.downloadingPdf = orderID;

      this.$http.post(this.getApiEndpoint('/orders/offer/' + orderID), {
        type: 'pdf'
      }, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        filename = filename.replace(/"/gmi, '');
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingPdf = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingPdf = false;
      });
    },
    downloadXls (orderID, orderStatus) {
      this.downloadingXls = orderID;

      this.$http.post(this.getApiEndpoint('/orders/offer/' + orderID), {
        type: 'xlsx'
      }, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filenamePrefix = this.$t('order.orderFileNamePrefix');

        if (orderStatusPhase['offer-file-prefix'].indexOf(orderStatus) > -1) {
          filenamePrefix = this.$t('order.offerFileNamePrefix');
        }

        link.setAttribute('download', filenamePrefix + orderID + '.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingXls = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingXls = false;
      });
    },
    // eslint-disable-next-line
    downloadSummary (orderID, orderName) {
      this.downloadingSummary = orderID;

      this.$http.post(this.getApiEndpoint('/orders/summary/' + orderID), {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        filename = filename.replace(/"/gmi, '');
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingSummary = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingSummary = false;
      });
    },
    downloadInvoice (orderData) {
      this.downloadingInvoice = orderData.id;
      let endpoint = orderData.settlements[0].file_url;
      // let fileName = orderData.settlements[0].number + '_' + orderData.order_name + '.pdf';

      this.$http.post(endpoint, {}, {
        responseType: 'blob'
      }).then(response => {
        let fileName = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        fileName = fileName.replace(/[\s\/\\]/gmi, '-', fileName);

        if (orderData.settlements.length > 1) {
          endpoint = orderData.settlements_zipped_files;
          fileName = 'faktury-do-zamowienia-' + orderData.id + '.zip';
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName.replace(/"/gmi, ''));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingInvoice = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingInvoice = false;
      });
    },
    downloadAttachment (orderID) {
      this.downloadingAttachment = orderID;

      this.$http.post(this.getApiEndpoint('/orders/download-order-attachment/' + orderID), {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        document.body.appendChild(link);
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        link.click();
        document.body.removeChild(link);
        this.downloadingAttachment = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingAttachment = false;
      });
    },
    getCurrency (currencyID) {
      return CommonGetters.getCurrencyById(currencyID, this.currencies);
    },
    getStatusName (status, orderIsCollectedBy) {
      let statusName = ListUtils.getKeyLabel(orderStatuses[this.$i18n.locale], status);

      if (status === 'TO_COLLECT' && orderIsCollectedBy === this.getUserID) {
        return this.$t('order.beeingCollected');
      }

      if (status === 'SENT_TO_CUSTOMER' && this.userIsCustomer) {
        return this.$t('order.forAcceptance');
      }

      return statusName;
    },
    getStatusClass (status) {
      return ListUtils.getKeyLabel(orderStatusClasses, status).replace(' outline', '');
    },
    getOutline (status, orderIsCollectedBy) {
      if (status === 'TO_COLLECT' && orderIsCollectedBy === this.getUserID) {
        return false;
      }

      return ListUtils.getKeyLabel(orderStatusClasses, status).indexOf(' outline') > -1;
    },
    setCustomerID (customerID) {
      this.filters.customer_id = customerID;
    },
    getServicesCount (products, services) {
      let productsCount = products.map(product => product.services.length).reduce((a, b) => a + b, 0);
      let servicesCount = 0;

      if (services && services.length > 0) {
        servicesCount = services.length;
      }

      return productsCount + servicesCount;
    },
    hasDeadlineWarning (order) {
      if (order.warnings && order.warnings.filter(warning => warning.type === 'DEADLINE').length) {
        return true;
      }

      return false;
    },
    hasShippingDateWarning (order) {
      if (order.shipping_date_warning) {
        return !!order.shipping_date_warning;
      }

      return false;
    },
    hasBlockadeDateWarning (order) {
      if (order.blockade_status === 'BLOCKADE' && order.blocked_at) {
        let blockadeDate = new Date(order.blocked_at).getTime();
        let currentDate = new Date().getTime();

        if (currentDate - blockadeDate > 7 * 24 * 60 * 60 * 1000) {
          return true;
        }
      }

      return false;
    },
    hasServicesWarnings (order) {
      if (order.warnings && order.warnings.filter(warning => warning.type !== 'DEADLINE').length) {
        return true;
      }

      return false;
    },
    hasUnpaidAdvance (order) {
      return this.userIsAdminOrWorker && order.is_advance && !order.advance_paid;
    },
    takeOrder (orderID) {
      this.takeOrderInProgress = orderID;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: this.getApiEndpoint('/orders/take-to-collect/' + orderID),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.orderCollectErrorText'),
        successAction: () => {
          this.takeOrderInProgress = false;

          if (this.isIndentList) {
            this.$router.push('/zamowienia-indent/edycja/' + parseInt(orderID, 10));
          } else {
            this.$router.push('/zamowienia/edycja/' + parseInt(orderID, 10));
          }
        },
        failAction: () => {
          this.takeOrderInProgress = false;
        }
      });
    },
    orderActionUrl (orderID, isIndent, newWindow = false) {
      if (this.isAllWarehouseOrdersView) {
        return this.$router.push('/zamowienia-wszystkie/podglad/' + orderID);
      }

      if (!newWindow) {
        if (this.isInquiriesView) {
          return this.$router.push('/zapytania/zapytanie/' + orderID);
        }

        if (this.isOffersView) {
          if (this.isIndentList) {
            return this.$router.push('/oferty-indent/edycja/' + orderID);
          }

          return this.$router.push('/oferty/edycja/' + orderID);
        }

        if (isIndent) {
          return this.$router.push('/zamowienia-indent/edycja/' + orderID);
        }

        return this.$router.push('/zamowienia/edycja/' + orderID);
      }

      let routeData = this.$router.resolve('/zamowienia/edycja/' + orderID);

      if (this.isIndentList) {
        routeData = this.$router.resolve('/zamowienia-indent/edycja/' + orderID);
      }

      if (this.isInquiriesView) {
        routeData = this.$router.resolve('/zapytania/zapytanie/' + orderID);
      }

      if (this.isOffersView) {
        if (this.isIndentList) {
          routeData = this.$router.resolve('/oferty-indent/edycja/' + orderID);
        } else {
          routeData = this.$router.resolve('/oferty/edycja/' + orderID);
        }
      }

      window.open(routeData.href, '_blank');
      return false;
    },
    prepareDateString (input) {
      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    prepareCustomerTagsFilter (customerTags) {
      let output = [];

      for (let i = 0; i < customerTags.length; i++) {
        let tagData = customerTags[i].value;
        output.push(tagData);
      }

      return output;
    },
    prepareOrderTagsFilter (orderTags) {
      let output = [];

      for (let i = 0; i < orderTags.length; i++) {
        let tagData = orderTags[i].value;
        output.push(tagData);
      }

      return output;
    },
    prepareProductTagsFilter (productTags) {
      let output = {};

      for (let i = 0; i < productTags.length; i++) {
        let tagData = productTags[i].value.split(';');
        let tagCategoryID = parseInt(tagData[0], 10);
        let tagID = parseInt(tagData[1], 10);

        if (output[tagCategoryID]) {
          output[tagCategoryID].push(tagID);
        } else {
          output[tagCategoryID] = [tagID];
        }
      }

      return output;
    },
    prepareProductCodesFilter (productCode) {
      let output = [];

      for (let i = 0; i < productCode.length; i++) {
        output.push(productCode[i].value.replace(/\[\#[0-9]{1,}\#\]/gmi, ''));
      }

      return output;
    },
    filterByProductCodes (option, label, search) {
      return (option.valueLowerCase).indexOf(search) > -1;
    },
    filteredProductCodes (options, search) {
      let searchLowerCase = search.toLowerCase();
      return options.filter(option => this.filterByProductCodes(option, option.id, searchLowerCase)).slice(0, 50);
    },
    showOrderStatusPopup (orderName, orderID, orderStatus) {
      if (
        (orderStatus === 'ENDED' && !this.userIsAdmin) ||
        orderStatus === 'DELETED' ||
        orderStatus === 'TO_DELETE'
      ) {
        Vue.swal({
          title: this.$t('status.operationNotPermitted'),
          html: this.$t('status.operationNotPermittedDesc'),
          type: 'error',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        return;
      }

      this.statusChangeOrderName = orderName;
      this.statusChangeOrderID = orderID;
      this.statusChangeOrderStatus = orderStatus;
      this.statusChangeInProgress = false;

      Vue.nextTick(() => {
        this.displayChangeStatusPopup = true;
      });
    },
    saveNewOrderStatus () {
      let selectedOrder = this.orders.filter(order => order.id === this.statusChangeOrderID);

      if (
        selectedOrder &&
        selectedOrder[0] &&
        !selectedOrder[0].settlements.length &&
        this.statusChangeOrderStatus === 'ENDED'
      ) {
        Vue.swal({
          title: this.$t('order.noSettlementsTitle'),
          text: this.$t('order.noSettlementsText'),
          type: 'question',
          showCancelButton: true,
          confirmButtonText: this.$t('status.yesChangeStatus'),
          cancelButtonText: this.$t('ui.cancel')
        }).then((result) => {
          if (result.value) {
            FormUtils.submit({
              $http: this.$http,
              endpoint: '/orders/statuses/change-status/' + this.statusChangeOrderID,
              formData: {
                status: this.statusChangeOrderStatus
              },
              successTitle: this.$t('status.changeStatusSuccessTitle'),
              successText: this.$t('status.changeStatusSuccessText'),
              errorTitle: this.$t('ui.errorOccured'),
              errorText: this.$t('status.changeStatusErrorText'),
              successAction: () => {
                this.statusChangeInProgress = false;
                this.displayChangeStatusPopup = false;
                this.debouncedLoadFilteredData();
              },
              failAction: () => {
                this.statusChangeInProgress = false;
              }
            });
          } else {
            this.statusChangeInProgress = false;
            this.displayChangeStatusPopup = false;
          }
        });
      } else {
        FormUtils.submit({
          $http: this.$http,
          endpoint: '/orders/statuses/change-status/' + this.statusChangeOrderID,
          formData: {
            status: this.statusChangeOrderStatus
          },
          successTitle: this.$t('status.changeStatusSuccessTitle'),
          successText: this.$t('status.changeStatusSuccessText'),
          errorTitle: this.$t('ui.errorOccured'),
          errorText: this.$t('status.changeStatusErrorText'),
          successAction: () => {
            this.statusChangeInProgress = false;
            this.displayChangeStatusPopup = false;
            this.debouncedLoadFilteredData();
          },
          failAction: () => {
            this.statusChangeInProgress = false;
          }
        });
      }
    },
    sendReminder (orderID, type) {
      this.reminderSendingInProgress = orderID;

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/orders/emails/send/' + orderID,
        formData: {
          type
        },
        successTitle: this.$t('order.reminderSentTitle'),
        successText: this.$t('order.reminderSentDesc'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.reminderErrorTitle'),
        successAction: () => {
          this.reminderSendingInProgress = false;
          this.debouncedLoadFilteredData();
        },
        failAction: () => {
          this.reminderSendingInProgress = false;
        }
      });
    },
    openOrderSummaryPopup (orderID) {
      this.showOrderSummaryPopup = true;
      this.orderSummaryID = orderID;
    },
    closeOrderSummaryPopup () {
      this.showOrderSummaryPopup = false;
    },
    formatDateStart (input) {
      if (input === null) {
        return this.$t('ui.searchFrom');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    formatDateEnd (input) {
      if (input === null) {
        return this.$t('ui.searchTo');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    getPurchaseStatus (status) {
      switch (status) {
        case 'NEW': return this.$t('purchase.newOrder');
        case 'WAITING_PRODUCTS': return this.$t('order.waitingPurchaseProducts');
        case 'WAITING_PAYMENT': return this.$t('order.pendingPayment');
        case 'CLOSED': return this.$t('purchase.closed');
        default: return '';
      }
    },
    loadFilteredDataOnUnblock (state) {
      if (!state) {
        this.loadFilteredData();
      }
    },
    confirmProductsReceive (orderID) {
      this.confirmPurchaseProductsInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/orders/receive-purchase-products/' + orderID,
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.orderPurchaseProductsConfirmErrorText'),
        successAction: (response) => {
          this.confirmPurchaseProductsInProgress = false;

          if (response.success) {
            if (this.isIndentList) {
              this.$router.push('/zamowienia-indent/edycja/' + orderID);
            } else {
              this.$router.push('/zamowienia/edycja/' + orderID);
            }
          } else {
            Vue.swal({
              title: this.$t('ui.errorOccured'),
              html: this.$t('order.orderPurchaseProductsConfirmErrorText'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.confirmPurchaseProductsInProgress = false;
        }
      });
    },
    complaintsStatus (complaints) {
      if (!complaints.length) {
        return false;
      }

      if (complaints.filter(complaint => complaint.status === 'IN_REALIZATION').length) {
        return 'IN_REALIZATION';
      }

      return 'ENDED';
    },
    downloadMarkingDoc (orderID) {
      this.downloadingMarkingInProgress = orderID;

      this.$http.post('/orders/box-marking/' + orderID, {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', this.$t('order.markingFileNamePrefix') + orderID + '.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingMarkingInProgress = false;

        if (this.isIndentList) {
          this.$router.push('/zamowienia-indent/edycja/' + parseInt(orderID, 10));
        } else {
          this.$router.push('/zamowienia/edycja/' + parseInt(orderID, 10));
        }
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingMarkingInProgress = false;
      });
    },
    retrieveLastViewedData () {
      if (!localStorage.getItem('headwear-crm-warehouse-last-viewed')) {
        return;
      }

      let data = localStorage.getItem('headwear-crm-warehouse-last-viewed').split(';');

      if (data[0] && data[1] && data[2] && parseInt(data[2], 10) > +new Date() - (1 * 60 * 1000)) {
        this.lastViewedItem = {
          action: data[0],
          id: parseInt(data[1], 10)
        };
      }

      localStorage.removeItem('headwear-crm-warehouse-last-viewed');
    }
  },
  beforeDestroy () {
    this.$bus.$off('ui-select-customer-updated', this.setCustomerID);
    this.$bus.$off('order-summary-popup-open', this.openOrderSummaryPopup);
    this.$bus.$off('order-summary-popup-close', this.closeOrderSummaryPopup);
    this.$bus.$off('order-unblock-in-progress', this.loadFilteredDataOnUnblock);
  }
};
</script>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}

.orders-filter {
  & > div {
    width: 100%;
  }

  flex-wrap: wrap;

  .form-control.is-select {
    border: none;
  }
}

tr.has-deadline {
  td:first-child {
    border-left: 8px solid #c4183c;
  }
}

tr.has-been-last-visited {
  td {
    border-top: 8px solid #ffb400;
  }
}

tr.has-shipping-warning {
  td {
    background: rgba(196, 24, 60, .1);
  }
}

tr.has-blockade-warning {
  td {
    background: rgba(255, 180, 0, .15);
  }
}

tr.has-unpaid-advance {
  td {
    background: #f22;
    color: #fff;

    & /deep/ span[class*="badge-outline"] {
      background: #fff;
    }

    & /deep/ button[class*="btn-outline"] {
      background: #fff;
    }

    & /deep/ .text-danger {
      color: #fff!important;
    }
  }
}

.customer-info {
  position: relative;

  .is-inquiry-info {
    display: inline-block;
    font-size: 10px;
    text-transform: uppercase;

    .material-icons {
      position: relative;
      top: 3px;
    }
  }
}

.purchase-info {
  border: 1px solid #ccc;
  border-radius: 5px;
  margin: 1rem 0 0 0;
  padding: .5rem;

  &-content {
    span {
      display: block;
      font-size: 12px;
      font-weight: normal;
    }
  }
}

body .orders-filter /deep/ .vs__dropdown-toggle {
  max-height: none!important;
}

.br-to-hide:first-child {
  display: none;
}

.is-moved-to-top {
  left: auto;
  position: absolute;
  right: 100px;
  top: -43px;
}
</style>
